import { paths } from "@kwp/firestore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, type ReactNode } from "react";
import { domainError, errorMessage, useTRPC } from "../../lib/api";
import { useClients } from "../../lib/clients";
import { MeProvider } from "../../lib/me";
import { Button, ErrorText, Splash } from "../../ui";
import { NotInvitedPage } from "./NotInvitedPage";
import { useSession } from "./session";
import { SignInPage } from "./SignInPage";

/**
 * Decides what a visitor sees: sign-in, "not invited", or the app. `me` enrols an invited user
 * on their first call (architecture §5), and the cache is cleared on sign-out so the next user starts fresh.
 */
export function Gate({ children }: { children: ReactNode }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const signedOut = session.status === "signed_out";

  useEffect(() => {
    if (signedOut) queryClient.clear();
  }, [signedOut, queryClient]);

  if (session.status === "loading") return <Splash label="Loading…" />;
  if (session.status === "signed_out") return <SignInPage />;
  return (
    <SignedIn uid={session.user.uid} email={session.user.email}>
      {children}
    </SignedIn>
  );
}

function SignedIn({ uid, email, children }: { uid: string; email: string | null; children: ReactNode }) {
  const trpc = useTRPC();
  // Transient failures are retried (the client's default); `not_invited` is a domain error, final.
  const me = useQuery(trpc.me.queryOptions());
  useWorkspaceMembershipRefresh(uid, me.data?.workspaces);

  if (me.isPending) return <Splash label="Loading your workspaces…" />;
  // A failed background refetch (the tab woke from sleep before the network) keeps the app and
  // the last good answer on screen; only a first load that failed shows the error.
  if (me.data === undefined) {
    if (domainError(me.error)?.reason === "not_invited") return <NotInvitedPage email={email} />;
    return (
      <main className="grid min-h-dvh place-items-center bg-bg px-4">
        <div className="flex max-w-sm flex-col gap-4">
          <ErrorText>{errorMessage(me.error)}</ErrorText>
          <Button onClick={() => void me.refetch()}>Try again</Button>
        </div>
      </main>
    );
  }
  return <MeProvider me={me.data}>{children}</MeProvider>;
}

/**
 * Someone else can change which workspaces you are in (an owner adds or removes you). Your own
 * `users/{uid}.workspaceIds` follows every such change, so a listener on it refetches `me` and the
 * Workspace picker updates without a reload.
 */
function useWorkspaceMembershipRefresh(uid: string, workspaces: ReadonlyArray<{ id: string }> | undefined) {
  const { firebase } = useClients();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const known = workspaces === undefined ? null : idsKey(workspaces.map((w) => w.id));

  useEffect(() => {
    if (known === null) return;
    return onSnapshot(
      doc(firebase.db, paths.user(uid)),
      (snap) => {
        const ids: unknown = snap.get("workspaceIds");
        if (!Array.isArray(ids) || idsKey(ids.map(String)) === known) return;
        void queryClient.invalidateQueries({ queryKey: trpc.me.queryKey() });
      },
      () => undefined,
    );
  }, [firebase.db, uid, known, queryClient, trpc]);
}

const idsKey = (ids: readonly string[]) => [...ids].sort().join(",");
